import { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  useCreateAdMutation,
  useEstimateAudienceMutation,
} from "../features/ads/adsApiSlice";
import { SuccessNotify, FailedNotify } from "../constants/tostify";
import { promotionSchema, getErrorMessage } from "./promotionValidation";

export { promotionSchema } from "./promotionValidation";

export const useCreatePromotion = () => {
  const { name } = useSelector((state) => state.user);
  const [estimatedAudience, setEstimatedAudience] = useState(0);

  const [createAd, { isLoading: isCreating }] = useCreateAdMutation();
  const [estimateAudienceApi, { isLoading: isEstimating }] =
    useEstimateAudienceMutation();

  /**
   * Validate the form and submit the ad draft to the Flask backend.
   * Calls SuccessNotify / FailedNotify internally.
   */
  const submitPromotion = useCallback(
    async (promoteShop, shopid) => {
      // ── Validation using Yup (shared with react-hook-form)
      try {
        await promotionSchema.validate(promoteShop, { abortEarly: false });
      } catch (validationError) {
        const firstError =
          validationError.inner?.[0]?.message || validationError.message;
        FailedNotify(firstError);
        return { success: false, error: firstError };
      }

      // Own user owns own ads — require logged-in user id
      const ownerUserId = name?._id;
      if (!ownerUserId) {
        FailedNotify("Please log in to create an advertisement.");
        return { success: false, error: "Not logged in" };
      }

      // ── Build FormData for multipart upload
      const shopID =
        localStorage.getItem("OpenShopIdForMarketingByMarketer") || shopid;

      const cleanLoc = (v) => {
        const t = String(v || "").trim();
        if (!t || /^select\s/i.test(t)) return "";
        return t;
      };
      const country = cleanLoc(promoteShop.location?.country);
      const state = cleanLoc(promoteShop.location?.state);
      const city = cleanLoc(promoteShop.location?.city);

      if (!country) {
        FailedNotify("Please select a country");
        return { success: false, error: "Please select a country" };
      }

      const formData = new FormData();
      // ownerUserId = logged-in user (scopes "my ads"); userId = shop/profile context
      formData.append("ownerUserId", ownerUserId);
      formData.append("userId", shopID || ownerUserId);
      formData.append(
        "userName",
        name?.fullname ||
          [name?.firstName, name?.lastName].filter(Boolean).join(" ") ||
          "",
      );
      formData.append("title", promoteShop.type);
      formData.append("description", (promoteShop.description || "").trim());
      formData.append("country", country);
      formData.append("state", state);
      formData.append("city", city);
      formData.append("placement", promoteShop.placement);
      formData.append("startDate", promoteShop.startDate || "");
      formData.append("endDate", promoteShop.endDate || "");
      formData.append("dailyBudget", Number(promoteShop.dailyBudget));
      formData.append("currency", promoteShop.currency || "USD");

      // Age is optional — only send when user fills it (empty = all ages on backend)
      if (promoteShop.ageMin !== "" && promoteShop.ageMin != null) {
        formData.append("ageMin", Number(promoteShop.ageMin));
      }
      if (promoteShop.ageMax !== "" && promoteShop.ageMax != null) {
        formData.append("ageMax", Number(promoteShop.ageMax));
      }

      // Gender optional — default "all" means every gender
      formData.append("gender", promoteShop.gender || "all");
      formData.append("language", promoteShop.language || "");

      formData.append(
        "platforms",
        JSON.stringify(promoteShop.businessTypes || []),
      );

      if (promoteShop.media.length > 0) {
        const paths = promoteShop.media
          .map((item) => {
            if (typeof item === "string") return item;
            if (item?.url || item?.path) return item.url || item.path;
            return null;
          })
          .filter(Boolean);

        const first = promoteShop.media[0];
        if (typeof File !== "undefined" && first instanceof File) {
          formData.append("media", first);
        }

        if (paths.length > 0) {
          formData.append("mediaPath", paths[0]);
          formData.append("mediaPaths", JSON.stringify(paths));
        }

        const mediaType =
          promoteShop.type === "video"
            ? "video"
            : promoteShop.type === "carousel"
              ? "carousel"
              : "image";
        formData.append("mediaType", mediaType);
      }

      try {
        const result = await createAd(formData).unwrap();
        if (result?.status === "success") {
          SuccessNotify(
            "Advertisement submitted — pending admin verification. Proceeding to payment...",
          );
          return {
            success: true,
            adId: result.ad_id,
            publicAdId: result.public_ad_id,
            budget: promoteShop.dailyBudget,
            workflow: result.workflow,
          };
        }
        const msg = result?.message || "Failed to create ad.";
        FailedNotify(msg);
        return { success: false, error: msg };
      } catch (err) {
        console.error("Create ad error:", err);
        const msg = getErrorMessage(
          err,
          "Failed to create ad. Please try again.",
        );
        FailedNotify(msg);
        return { success: false, error: msg };
      }
    },
    [createAd, name],
  );

  const estimateAudience = useCallback(
    async (promoteShop) => {
      try {
        const country = promoteShop.location?.country;
        const cleanCountry =
          !country || /^select/i.test(String(country).trim())
            ? ""
            : String(country).trim();

        const dailyBudget = Number(promoteShop.dailyBudget) || 0;
        const currencyRate =
          Number(promoteShop.currencyRate || promoteShop.rate) || 1;
        const currency = promoteShop.currency || "USD";

        // If dailyBudget is present, provide immediate optimistic estimate
        if (dailyBudget > 0) {
          const budgetUSD = dailyBudget / currencyRate;
          setEstimatedAudience(Math.max(10, Math.round(budgetUSD * 75)));
        } else if (!cleanCountry) {
          setEstimatedAudience(0);
          return;
        }

        const targeting = {
          country: cleanCountry,
          state: promoteShop.location?.state || "",
          city: promoteShop.location?.city || "",
          gender: promoteShop.gender || "all",
          age: {
            min:
              promoteShop.ageMin !== "" && promoteShop.ageMin != null
                ? Number(promoteShop.ageMin)
                : undefined,
            max:
              promoteShop.ageMax !== "" && promoteShop.ageMax != null
                ? Number(promoteShop.ageMax)
                : undefined,
          },
          platforms: promoteShop.businessTypes || [],
          dailyBudget,
          currency,
          currencyRate,
        };

        const result = await estimateAudienceApi(targeting).unwrap();
        if (typeof result?.estimatedAudience === "number") {
          setEstimatedAudience(result.estimatedAudience);
        }
      } catch (err) {
        console.error("Estimate audience error:", err);
        const dailyBudget = Number(promoteShop.dailyBudget) || 0;
        const currencyRate =
          Number(promoteShop.currencyRate || promoteShop.rate) || 1;
        if (dailyBudget > 0) {
          const budgetUSD = dailyBudget / currencyRate;
          setEstimatedAudience(Math.max(10, Math.round(budgetUSD * 75)));
        } else {
          setEstimatedAudience(0);
        }
      }
    },
    [estimateAudienceApi],
  );

  return {
    submitPromotion,
    estimateAudience,
    estimatedAudience,
    isSubmitting: isCreating,
    isEstimating,
  };
};
